import { SECURITY_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { CURRENCIES_GROUPED_OPTIONS } from '@dealstack/frontend/shared/currencies.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	drawerTransitionProps,
	groupedSelectDividerStyles,
} from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { CurrencyType, SecurityType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Drawer } from '@mantine/core';
import { memo, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import styled from 'styled-components';

import {
	CreateSecurityFormData,
	CreateSecurityFormShape,
	CreateSecurityFormValues,
	SubmitOptions,
} from './CreateSecurityDrawer.types';

export enum CREATE_SECURITY_FORM_FIELD {
	name = 'name',
	type = 'type',
	currency = 'currency',
}

interface Props {
	className?: string;
	opened: boolean;
	onClose: () => void;
	onCreate: (security: CreateSecurityFormData, options: SubmitOptions) => void;
	isLoading?: boolean;
	defaultCurrency?: CurrencyType;
	/** Preselects a type; omit to make the user choose one. */
	defaultType?: SecurityType;
	/** Locks the currency to the one the issuer already holds securities in. */
	lockCurrency?: boolean;
}

const CreateSecurityDrawer = ({
	opened,
	onClose,
	onCreate,
	isLoading = false,
	defaultCurrency,
	defaultType,
	lockCurrency = false,
	className,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const formRef = useRef<HTMLFormElement>(null);

	const defaultValues = useMemo(
		(): CreateSecurityFormValues => ({
			[CREATE_SECURITY_FORM_FIELD.name]: '',
			[CREATE_SECURITY_FORM_FIELD.type]: defaultType ?? null,
			[CREATE_SECURITY_FORM_FIELD.currency]: defaultCurrency ?? null,
		}),
		[defaultCurrency, defaultType]
	);

	const createSecurityForm = useForm<CreateSecurityFormValues>({
		mode: 'all',
		resolver: zodResolver(CreateSecurityFormShape),
		defaultValues,
		shouldFocusError: false,
	});

	const wasOpened = useRef(false);

	useEffect(() => {
		// Reset only when the drawer opens; parent re-renders while open must not wipe user input.
		if (opened && !wasOpened.current) {
			createSecurityForm.reset(defaultValues);
		}
		wasOpened.current = opened;
	}, [createSecurityForm, defaultValues, opened]);

	useEffect(() => {
		if (defaultCurrency) {
			createSecurityForm.setValue(CREATE_SECURITY_FORM_FIELD.currency, defaultCurrency);
		}
	}, [createSecurityForm, defaultCurrency]);

	const submit =
		(shouldCloseOnSuccess: boolean) =>
		(formValues: CreateSecurityFormValues): void => {
			if (!formValues.type || !formValues.currency) {
				return;
			}

			const security: CreateSecurityFormData = {
				name: formValues.name,
				type: formValues.type,
				currency: formValues.currency,
			};

			onCreate(security, {
				shouldCloseOnSuccess,
				resetForm: () => createSecurityForm.reset(defaultValues),
			});
		};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			className={className}
			transitionProps={{
				...drawerTransitionProps,
				onEntered: () => formRef.current?.setAttribute('data-drawer-ready', ''),
				onExited: () => formRef.current?.removeAttribute('data-drawer-ready'),
			}}
			trapFocus={false}
			title={tCapManage('securities.drawer.add_title')}
		>
			{isLoading && <InlineLoader />}
			<FormProvider {...createSecurityForm}>
				<form ref={formRef} onSubmit={createSecurityForm.handleSubmit(submit(true))}>
					<Select
						name={CREATE_SECURITY_FORM_FIELD.type}
						control={createSecurityForm.control}
						label={tCapManage('securities.drawer.type.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_CREATE_SECURITY_TYPE_SELECT}
						placeholder={tCapManage('securities.drawer.type.placeholder')}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						data={SECURITY_TYPE_OPTIONS}
						disabled={isLoading}
					/>
					<TextInput
						name={CREATE_SECURITY_FORM_FIELD.name}
						control={createSecurityForm.control}
						label={tCapManage('securities.drawer.name.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_CREATE_SECURITY_NAME_INPUT}
						size="md"
						placeholder={tCapManage('securities.drawer.name.placeholder')}
						disabled={isLoading}
					/>
					<Select
						name={CREATE_SECURITY_FORM_FIELD.currency}
						control={createSecurityForm.control}
						label={tCapManage('securities.drawer.currency.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_CREATE_SECURITY_CURRENCY_SELECT}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						placeholder={tCapManage('securities.drawer.currency.placeholder')}
						data={CURRENCIES_GROUPED_OPTIONS}
						disabled={lockCurrency || isLoading}
						styles={groupedSelectDividerStyles}
					/>

					<Button
						fullWidth
						mt="auto"
						type="submit"
						loading={isLoading}
						data-testid={TEST_ELEMENT_IDS.CAP_CREATE_SECURITY_SUBMIT_BUTTON}
					>
						{tCapManage('securities.drawer.create_button')}
					</Button>

					<Button
						onClick={createSecurityForm.handleSubmit(submit(false))}
						fullWidth
						variant="default"
						disabled={isLoading}
					>
						{tCapManage('securities.drawer.create_add_another_button')}
					</Button>
				</form>
			</FormProvider>
		</Drawer>
	);
};

const Memoized = memo(CreateSecurityDrawer);
const Styled = styled(Memoized)`
	form {
		height: 100%;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;

export { Styled as CreateSecurityDrawer };
